<script>
import Vue from 'vue'
import {
    getPromoCodes,
    createPromoCode,
    updatePromoCode,
    deletePromoCode,
    getPromoCodeLink,
} from '@/api/landingPromoCode';

/**
 * Промокоди landing-акаунта на сторінці редагування точки продажу.
 * Відсоток знижки довільний. При калькуляції промокод застосовується лише
 * до полісів, де знижка не перевищує заробіток точки продажу (мінус
 * еквайринг, якщо його платить агент); решта продаються без знижки з
 * повідомленням «Краща ціна».
 */
export default Vue.extend({
    name: "LandingPromoCodes",
    props: {
        partnerId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            promoCodes: [],
            productsTypes: [],
            errors: null,
            form: {
                code: '',
                discountPercent: null,
                expiresAt: null,
                products: [],
            },
            linkDialog: {
                visible: false,
                code: '',
                url: '',
                qrcode: '',
            },
        }
    },
    methods: {
        getError(key) {
            return this.errors[key][0];
        },
        hasError(key) {
            return _.has(this.errors, key);
        },
        async load() {
            this.loading = true;
            try {
                const response = await getPromoCodes(this.partnerId);
                this.promoCodes = response.data.data.promoCodes || [];
                this.productsTypes = response.data.data.productsTypes || [];
            } catch (error) {
                this.$message.error('Не вдалося завантажити промокоди');
            }
            this.loading = false;
        },
        async create() {
            this.errors = null;
            this.saving = true;
            try {
                await createPromoCode(this.partnerId, {
                    code: this.form.code,
                    discountPercent: this.form.discountPercent,
                    expiresAt: this.form.expiresAt,
                    products: this.form.products,
                });
                this.$message.success('Промокод створено');
                this.form.code = '';
                this.form.discountPercent = null;
                this.form.expiresAt = null;
                this.form.products = [];
                await this.load();
            } catch (error) {
                this.handleError(error);
            }
            this.saving = false;
        },
        async toggleActive(promoCode) {
            this.saving = true;
            try {
                await updatePromoCode(this.partnerId, promoCode.id, {
                    isActive: !promoCode.isActive,
                });
                await this.load();
            } catch (error) {
                this.handleError(error);
            }
            this.saving = false;
        },
        async remove(promoCode) {
            try {
                await this.$confirm(`Видалити промокод «${promoCode.code}»?`, {type: 'warning'});
            } catch (e) {
                return;
            }

            this.saving = true;
            try {
                await deletePromoCode(this.partnerId, promoCode.id);
                this.$message.success('Промокод видалено');
                await this.load();
            } catch (error) {
                this.handleError(error);
            }
            this.saving = false;
        },
        async showLink(promoCode, productCode) {
            this.saving = true;
            try {
                const response = await getPromoCodeLink(this.partnerId, promoCode.id, productCode);
                this.linkDialog = {
                    visible: true,
                    code: promoCode.code,
                    url: response.data.data.url,
                    qrcode: response.data.data.qrcode,
                };
            } catch (error) {
                this.handleError(error);
            }
            this.saving = false;
        },
        copyLink() {
            navigator.clipboard.writeText(this.linkDialog.url).then(() => {
                this.$message.success('Посилання скопійовано');
            }).catch(() => {
                this.$message.error('Не вдалося скопіювати');
            });
        },
        handleError(error) {
            const responseData = error.response?.data;

            if (responseData?.status === 'validation' && responseData?.data) {
                this.errors = responseData.data;

                const allMessages = Object.values(responseData.data)
                    .flat()
                    .join('<br><br>');

                this.$message.error({
                    dangerouslyUseHTMLString: true,
                    message: allMessages
                });
            } else if (responseData?.message) {
                this.$message.error(responseData.message);
            } else {
                this.$message.error('Сталася помилка. Спробуйте ще раз.');
            }
        },
        getPromoCodeProducts(promoCode) {
            if (!promoCode.products || !promoCode.products.length) return [];
            return this.productsTypes.filter(pt => promoCode.products.includes(pt.id));
        },
        handleCommand(command, promoCode) {
            if (command.type === 'qr') {
                this.showLink(promoCode, command.productCode);
            } else if (command.type === 'delete') {
                this.remove(promoCode);
            }
        },
    },
    mounted() {
        this.load();
    },
})
</script>

<template>
    <div class="history-order__form-group" v-loading="loading">
        <div class="history-order__form-heading">Промокоди лендингу:</div>

        <el-alert
            type="info"
            :closable="false"
            class="mb-3"
            title="Знижка надається коштом заробітку точки продажу. Якщо по полісу знижка перевищує заробіток (мінус еквайринг, якщо його платить агент) або заробітку немає, промокод до нього не застосується — клієнт побачить «Краща ціна. На цей поліс промокод не застосовується».">
        </el-alert>

        <div class="row">
            <div class="col-md-3">
                <label>Код</label>
                <input
                    autocomplete="off"
                    v-model="form.code"
                    @input="form.code = form.code.replace(/[^a-zA-Z0-9\-_]/g, '').toUpperCase()"
                    type="text"
                    placeholder="SUMMER-10"
                    class="form-control"
                    :class="{'is-invalid': hasError('code') }">
                <div v-if="hasError('code')" class="invalid-feedback">{{ getError('code') }}</div>
            </div>
            <div class="col-md-2">
                <label>Знижка, %</label>
                <input
                    autocomplete="off"
                    v-model.number="form.discountPercent"
                    type="number"
                    min="0.01"
                    max="100"
                    step="0.01"
                    class="form-control"
                    :class="{'is-invalid': hasError('discountPercent') }">
                <div v-if="hasError('discountPercent')" class="invalid-feedback">{{ getError('discountPercent') }}</div>
            </div>
            <div class="col-md-3">
                <label>Продукти</label>
                <el-select 
                    v-model="form.products" 
                    multiple 
                    collapse-tags 
                    placeholder="Оберіть продукти"
                    class="w-100"
                    :class="{'is-invalid': hasError('products')}">
                    <el-option
                        v-for="item in productsTypes"
                        :key="item.id"
                        :label="item.title"
                        :value="item.id"
                        :disabled="!item.isActive">
                    </el-option>
                </el-select>
                <div v-if="hasError('products')" class="invalid-feedback d-block">{{ getError('products') }}</div>
            </div>
            <div class="col-md-2">
                <label class="d-block mb-0">Діє до (не об.)</label>
                <el-date-picker
                    v-model="form.expiresAt"
                    type="date"
                    value-format="yyyy-MM-dd"
                    format="dd.MM.yyyy"
                    placeholder="Безстроково"
                    :picker-options="{disabledDate: (date) => date < new Date(new Date().setHours(0,0,0,0))}"
                    class="w-100">
                </el-date-picker>
                <div v-if="hasError('expiresAt')" class="invalid-feedback d-block">{{ getError('expiresAt') }}</div>
            </div>
            <div class="col-md-2 d-flex align-items-center">
                <button
                    class="btn btn-blue-w"
                    :disabled="saving"
                    @click="create">
                    Створити промокод
                </button>
            </div>
        </div>

        <div class="table-responsive" v-if="promoCodes.length">
            <table class="table mt-3 promo-table">
                <thead>
                    <tr>
                        <th>Код</th>
                        <th>Знижка, %</th>
                        <th>Діє до</th>
                        <th>Активний</th>
                        <th class="text-right action-cell"></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="promoCode in promoCodes">
                        <tr :key="'row_' + promoCode.id">
                            <td>{{ promoCode.code }}</td>
                            <td>{{ promoCode.discountPercent }}</td>
                            <td>
                                <span v-if="!promoCode.expiresAt">Безстроково</span>
                                <span v-else :class="{'text-danger': promoCode.isExpired}">
                                    {{ promoCode.expiresAt.split('-').reverse().join('.') }}
                                    <template v-if="promoCode.isExpired">(прострочений)</template>
                                </span>
                            </td>
                            <td>
                                <el-switch
                                    :value="promoCode.isActive"
                                    :disabled="saving"
                                    @change="toggleActive(promoCode)">
                                </el-switch>
                            </td>
                            <td class="text-right action-cell">
                                <!-- Desktop Actions (>= 677px) -->
                                <div class="desktop-actions">
                                    <el-dropdown trigger="click" @command="(productCode) => showLink(promoCode, productCode)">
                                        <button class="btn-grey btn-sm mr-2" :disabled="saving || getPromoCodeProducts(promoCode).length === 0">
                                            QR / Посилання <i class="el-icon-arrow-down el-icon--right"></i>
                                        </button>
                                        <el-dropdown-menu slot="dropdown">
                                            <el-dropdown-item 
                                                v-for="pt in getPromoCodeProducts(promoCode)" 
                                                :key="pt.id" 
                                                :command="pt.code">
                                                {{ pt.title }}
                                            </el-dropdown-item>
                                        </el-dropdown-menu>
                                    </el-dropdown>
                                    <button class="btn-grey btn-sm" :disabled="saving" @click="remove(promoCode)">
                                        Видалити
                                    </button>
                                </div>

                                <!-- Tablet Actions (508px - 676px) -->
                                <div class="tablet-actions">
                                    <el-dropdown trigger="click" @command="(cmd) => handleCommand(cmd, promoCode)">
                                        <button class="btn-grey btn-sm" :disabled="saving">
                                            Дії <i class="el-icon-more el-icon--right"></i>
                                        </button>
                                        <el-dropdown-menu slot="dropdown">
                                            <!-- Показуємо продукти для QR -->
                                            <el-dropdown-item 
                                                v-for="pt in getPromoCodeProducts(promoCode)" 
                                                :key="'qr_' + pt.id" 
                                                :command="{ type: 'qr', productCode: pt.code }">
                                                <i class="el-icon-link"></i> QR / Посилання: {{ pt.title }}
                                            </el-dropdown-item>
                                            
                                            <!-- Якщо немає продуктів, показуємо відключений пункт -->
                                            <el-dropdown-item v-if="getPromoCodeProducts(promoCode).length === 0" disabled>
                                                <i class="el-icon-link"></i> QR / Посилання (немає продуктів)
                                            </el-dropdown-item>
                                            
                                            <el-dropdown-item divided disabled></el-dropdown-item>
                                            
                                            <el-dropdown-item :command="{ type: 'delete' }" class="text-danger">
                                                <i class="el-icon-delete"></i> Видалити
                                            </el-dropdown-item>
                                        </el-dropdown-menu>
                                    </el-dropdown>
                                </div>
                            </td>
                        </tr>
                        
                        <!-- Mobile Actions Row (<= 507px) -->
                        <tr :key="'mobile_actions_' + promoCode.id" class="mobile-action-row">
                            <td colspan="4" class="pt-0 border-top-0">
                                <el-dropdown trigger="click" @command="(cmd) => handleCommand(cmd, promoCode)" class="w-100">
                                    <button class="btn-grey btn-sm w-100" :disabled="saving">
                                        Дії <i class="el-icon-more el-icon--right"></i>
                                    </button>
                                    <el-dropdown-menu slot="dropdown" class="w-100 text-center">
                                        <el-dropdown-item 
                                            v-for="pt in getPromoCodeProducts(promoCode)" 
                                            :key="'mqr_' + pt.id" 
                                            :command="{ type: 'qr', productCode: pt.code }">
                                            <i class="el-icon-link"></i> QR / Посилання: {{ pt.title }}
                                        </el-dropdown-item>
                                        
                                        <el-dropdown-item v-if="getPromoCodeProducts(promoCode).length === 0" disabled>
                                            <i class="el-icon-link"></i> QR / Посилання (немає продуктів)
                                        </el-dropdown-item>
                                        
                                        <el-dropdown-item divided disabled></el-dropdown-item>
                                        
                                        <el-dropdown-item :command="{ type: 'delete' }" class="text-danger">
                                            <i class="el-icon-delete"></i> Видалити
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <small v-else class="text-muted d-block mt-2">Промокодів ще немає.</small>

        <el-dialog
            :title="`Промокод ${linkDialog.code}`"
            :visible.sync="linkDialog.visible"
            width="420px">
            <div class="text-center">
                <img v-if="linkDialog.qrcode" :src="linkDialog.qrcode" alt="QR" style="width: 260px; height: 260px;">
                <div class="mt-2" style="word-break: break-all;">
                    <a :href="linkDialog.url" target="_blank" rel="noopener">{{ linkDialog.url }}</a>
                </div>
                <div class="mt-3">
                    <button class="btn-blue-w dialog-btn mr-2" @click="copyLink">Скопіювати посилання</button>
                    <a
                        v-if="linkDialog.qrcode"
                        class="btn-grey dialog-btn"
                        :href="linkDialog.qrcode"
                        :download="`promo-${linkDialog.code}.png`">
                        Завантажити QR
                    </a>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<style scoped>
.dialog-btn {
    display: inline-block;
    padding: 10px 20px;
    font-size: 16px;
}

.desktop-actions {
    display: inline-block;
}

.tablet-actions {
    display: none;
}

.action-cell {
    display: table-cell;
}

.mobile-action-row {
    display: none;
}

@media (max-width: 676px) {
    .desktop-actions {
        display: none !important;
    }
    .tablet-actions {
        display: inline-block !important;
    }
}

@media (max-width: 507px) {
    .tablet-actions {
        display: none !important;
    }
    .action-cell {
        display: none !important;
    }
    .mobile-action-row {
        display: table-row !important;
    }
}
</style>
